import React, { useState, useEffect } from 'react';
import schema from './schema';
import defaultData from './defaultData';
import validator from './validator';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheckCircle, FiAlertTriangle, FiPrinter, FiSave, FiSettings } = FiIcons;

const InstallationCommissioningDecommissioningForm = ({ formInstanceId, onSave, onGeneratePDF }) => {
  const [formData, setFormData] = useState(defaultData);
  const [validation, setValidation] = useState({ isValid: true, errors: {}, warnings: {} });
  const [activeTab, setActiveTab] = useState('contractorDetails');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const result = validator.validate(formData);
    setValidation(result);
  }, [formData]);

  const handleChange = (sectionId, fieldId, value) => {
    setFormData(prev => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        [fieldId]: value
      }
    }));
  };

  const handleSave = () => {
    setIsSaving(true);
    if (onSave) onSave(formData);
    setTimeout(() => setIsSaving(false), 500);
  };

  const currentSection = schema.sections.find(s => s.id === activeTab) || schema.sections[0];

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Banner */}
      <div className="bg-gradient-to-r from-slate-900 via-zinc-900 to-slate-800 text-white p-6 rounded-xl shadow-lg mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <span className="bg-slate-700 text-slate-200 text-xs font-semibold px-2.5 py-0.5 rounded border border-slate-600">
            Multi-Trade Plant & Equipment Handover
          </span>
          <h1 className="text-2xl font-bold tracking-tight mt-1">Installation, Commissioning & Decommissioning</h1>
          <p className="text-slate-300 text-sm mt-1">Safe Isolation, Environmental Recovery & Statutory Commissioning Handover Record</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all"
          >
            <SafeIcon icon={FiSave} className="w-4 h-4" />
            {isSaving ? 'Saving...' : 'Save Draft'}
          </button>
          <button
            type="button"
            onClick={() => onGeneratePDF && onGeneratePDF(formData)}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all"
          >
            <SafeIcon icon={FiPrinter} className="w-4 h-4" />
            Generate PDF
          </button>
        </div>
      </div>

      {/* Activity HUD */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-slate-800 text-white p-4 rounded-xl border border-slate-700">
          <div className="text-xs text-slate-400 uppercase font-semibold">Activity Type</div>
          <div className="text-xl font-bold text-slate-200 mt-1">
            {formData.scopeOfWorks?.workActivityType?.split(' ')[0]}
          </div>
          <div className="text-xs text-slate-400 mt-1">{formData.scopeOfWorks?.equipmentCategory?.split('/')[0]}</div>
        </div>

        <div className="bg-slate-800 text-white p-4 rounded-xl border border-slate-700">
          <div className="text-xs text-slate-400 uppercase font-semibold">Safe Isolation</div>
          <div className="text-xl font-bold text-emerald-400 mt-1">
            {formData.safeIsolationAndEnvironment?.safeIsolationCompleted?.split(' ')[0] || 'Verified'}
          </div>
          <div className="text-xs text-slate-400 mt-1">F-Gas: {formData.safeIsolationAndEnvironment?.fGasRecovered?.split(' ')[0]}</div>
        </div>

        <div className="bg-slate-800 text-white p-4 rounded-xl border border-slate-700">
          <div className="text-xs text-slate-400 uppercase font-semibold">Operating Values</div>
          <div className="text-xl font-bold text-cyan-400 mt-1">
            {formData.commissioningReadings?.operatingPressureBar || 0} Bar
          </div>
          <div className="text-xs text-slate-400 mt-1">Flow Temp: {formData.commissioningReadings?.flowTemperatureC || 0}°C</div>
        </div>

        <div className="bg-slate-800 text-white p-4 rounded-xl border border-slate-700">
          <div className="text-xs text-slate-400 uppercase font-semibold">Completion Status</div>
          <div className="flex items-center gap-2 mt-1">
            {validation.isValid ? (
              <span className="flex items-center gap-1.5 text-emerald-400 font-bold text-lg">
                <SafeIcon icon={FiCheckCircle} className="w-5 h-5" /> Completed
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-amber-400 font-bold text-lg">
                <SafeIcon icon={FiAlertTriangle} className="w-5 h-5" /> Action Required
              </span>
            )}
          </div>
          <div className="text-xs text-slate-400 mt-1">{formData.handoverAndSignatures?.completionStatus}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex overflow-x-auto gap-2 border-b border-slate-200 dark:border-slate-800 pb-3 mb-6 scrollbar-thin">
        {schema.sections.map(section => (
          <button
            key={section.id}
            type="button"
            onClick={() => setActiveTab(section.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === section.id
                ? 'bg-slate-700 text-white shadow'
                : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700'
            }`}
          >
            {section.title}
          </button>
        ))}
      </div>

      {/* Fields */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm mb-6">
        <h2 className="text-lg font-bold text-slate-800 dark:text-white mb-4 pb-2 border-b border-slate-200 dark:border-slate-800">
          {currentSection.title}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {currentSection.fields.map(field => {
            const fieldKey = `${currentSection.id}.${field.id}`;
            const error = validation.errors[fieldKey];
            const warning = validation.warnings[fieldKey];
            const value = formData[currentSection.id]?.[field.id] ?? '';

            return (
              <div key={field.id} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                  {field.label} {field.required && <span className="text-red-500">*</span>}
                </label>

                {field.type === 'select' ? (
                  <select
                    value={value}
                    onChange={(e) => handleChange(currentSection.id, field.id, e.target.value)}
                    className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white"
                  >
                    {field.options?.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                ) : field.type === 'textarea' ? (
                  <textarea
                    value={value}
                    rows={3}
                    onChange={(e) => handleChange(currentSection.id, field.id, e.target.value)}
                    className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white"
                  />
                ) : field.type === 'signature' ? (
                  <div className="border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-lg p-4 text-center">
                    <input
                      type="text"
                      placeholder="Type name for digital signature stamp"
                      value={value}
                      onChange={(e) => handleChange(currentSection.id, field.id, e.target.value)}
                      className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white mb-2"
                    />
                  </div>
                ) : (
                  <input
                    type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                    step={field.type === 'number' ? 'any' : undefined}
                    value={value}
                    onChange={(e) => handleChange(currentSection.id, field.id, e.target.value)}
                    className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white"
                  />
                )}

                {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
                {warning && <p className="text-xs text-amber-500 mt-1">{warning}</p>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default InstallationCommissioningDecommissioningForm;
